import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Autocomplete,
    Collapse,
} from '@mui/material';
import {
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    ExpandMore,
    ExpandLess,
    Save,
} from '@mui/icons-material';
import { BasicButton } from '../../../../../components/BasicButton';
import apiClient from '../../../../../services/api/ApiClient';
import { ApiEndpoints } from '../../../../../config/constants';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';
import { PermissionWrapper } from '../../../../../components/PermissionWrapper';
import { HeaderOverlayConfig } from '../../../../../services/config.service';

interface GoogleFont {
    name: string;
    family?: string; // Some responses use family, some use name
    category: string;
}

// Track loaded fonts to avoid duplicate loads
const loadedFonts = new Set<string>();

// Helper to load a Google Font
const loadGoogleFont = (fontName: string) => {
    if (loadedFonts.has(fontName)) return;
    loadedFonts.add(fontName);
    
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
};

export const HeaderTextOverlay: React.FC = () => {
    const { config, reloadConfig } = useStoreConfig();
    
    // State
    const [overlay, setOverlay] = useState<HeaderOverlayConfig>(config.headerOverlay);
    const [originalOverlay, setOriginalOverlay] = useState<HeaderOverlayConfig>(config.headerOverlay);
    const [fonts, setFonts] = useState<GoogleFont[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Check for changes
    const hasChanges = JSON.stringify(overlay) !== JSON.stringify(originalOverlay);
    
    // Load fonts
    useEffect(() => {
        const loadFonts = async () => {
            try {
                const response = await apiClient.get<{ fonts: GoogleFont[] }>('/customization/fonts?limit=200');
                setFonts(response.data.fonts || []);
            } catch (err) {
                console.error('Failed to load fonts:', err);
            }
        };
        loadFonts();
    }, []);
    
    // Load Google Font stylesheet for preview
    useEffect(() => {
        if (overlay.font) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(overlay.font)}:wght@${overlay.fontWeight}&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
    }, [overlay.font, overlay.fontWeight]);
    
    // Update handler
    const updateOverlay = useCallback((updates: Partial<HeaderOverlayConfig>) => {
        setOverlay(prev => ({ ...prev, ...updates }));
    }, []);
    
    // Save handler
    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        
        try {
            await apiClient.patch(ApiEndpoints.AppHeader.UpdateOverlay, { overlay });
            setSuccess('Header overlay saved successfully!');
            setOriginalOverlay(overlay);
            await reloadConfig();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save overlay');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            {/* Alerts */}
            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}
            
            {/* Action Bar */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={overlay.enabled}
                            onChange={(e) => updateOverlay({ enabled: e.target.checked })}
                            color='primary'
                        />
                    }
                    label='Enable'
                    sx={{ mr: 'auto' }}
                />
                
                {hasChanges && (
                    <PermissionWrapper screen='app-header' permission='update'>
                        <BasicButton
                            title='Save'
                            variant='contained'
                            onClick={handleSave}
                            isLoading={isSaving}
                            startIcon={<Save fontSize='small' />}
                            sx={{ height: 32, fontSize: '0.75rem', px: 2 }}
                        />
                    </PermissionWrapper>
                )}
            </Box>
                
            <Collapse in={overlay.enabled}>
                    {/* Text Input */}
                    <TextField
                        fullWidth
                        label='Overlay Text'
                        value={overlay.text}
                        onChange={(e) => updateOverlay({ text: e.target.value })}
                        multiline
                        rows={2}
                        sx={{ mb: 3 }}
                        placeholder='Enter your headline text...'
                    />
                    
                    {/* Font & Color Row */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        {/* Font Selector */}
                        <Autocomplete
                            sx={{ flex: 1, minWidth: 200 }}
                            options={fonts}
                            getOptionLabel={(option) => option.name || option.family || ''}
                            value={fonts.find(f => (f.name || f.family) === overlay.font) || null}
                            onChange={(_, newValue) => updateOverlay({ font: newValue?.name || newValue?.family || null })}
                            isOptionEqualToValue={(option, value) => (option.name || option.family) === (value.name || value.family)}
                            renderInput={(params) => (
                                <TextField {...params} label='Font Family' size='small' />
                            )}
                            renderOption={(props, option) => {
                                const fontName = option.name || option.family || '';
                                // Load font when option is rendered
                                loadGoogleFont(fontName);
                                return (
                                    <li {...props} key={fontName}>
                                        <span style={{ fontFamily: `'${fontName}', sans-serif`, fontSize: 15 }}>
                                            {fontName}
                                        </span>
                                    </li>
                                );
                            }}
                            ListboxProps={{
                                style: { maxHeight: 300 }
                            }}
                        />
                        
                        {/* Color Picker */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant='caption' color='text.secondary'>Color:</Typography>
                            <input
                                type='color'
                                value={overlay.color}
                                onChange={(e) => updateOverlay({ color: e.target.value })}
                                style={{
                                    width: 40,
                                    height: 32,
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                }}
                            />
                            <TextField
                                size='small'
                                value={overlay.color}
                                onChange={(e) => updateOverlay({ color: e.target.value })}
                                sx={{ width: 100 }}
                            />
                        </Box>
                    </Box>
                    
                    {/* Size & Weight */}
                    <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant='caption' color='text.secondary' gutterBottom>
                                Font Size: {overlay.fontSize}rem
                            </Typography>
                            <Slider
                                value={overlay.fontSize}
                                onChange={(_, value) => updateOverlay({ fontSize: value as number })}
                                min={1}
                                max={8}
                                step={0.25}
                                marks={[
                                    { value: 1, label: '1' },
                                    { value: 4, label: '4' },
                                    { value: 8, label: '8' },
                                ]}
                            />
                        </Box>
                        
                        <FormControl size='small' sx={{ minWidth: 120 }}>
                            <InputLabel>Weight</InputLabel>
                            <Select
                                value={overlay.fontWeight}
                                label='Weight'
                                onChange={(e) => updateOverlay({ fontWeight: e.target.value as number })}
                            >
                                {[400, 500, 600, 700, 800, 900].map(weight => (
                                    <MenuItem key={weight} value={weight}>{weight}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    
                    {/* Alignment */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
                            Text Alignment
                        </Typography>
                        <ToggleButtonGroup
                            value={overlay.textAlign}
                            exclusive
                            onChange={(_, value) => value && updateOverlay({ textAlign: value })}
                            size='small'
                        >
                            <ToggleButton value='left'>
                                <FormatAlignLeft />
                            </ToggleButton>
                            <ToggleButton value='center'>
                                <FormatAlignCenter />
                            </ToggleButton>
                            <ToggleButton value='right'>
                                <FormatAlignRight />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    
                    {/* Position */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' color='text.secondary' gutterBottom>
                            Position (X: {overlay.position.x}%, Y: {overlay.position.y}%)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant='caption'>Horizontal</Typography>
                                <Slider
                                    value={overlay.position.x}
                                    onChange={(_, value) => updateOverlay({ 
                                        position: { ...overlay.position, x: value as number } 
                                    })}
                                    min={0}
                                    max={100}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant='caption'>Vertical</Typography>
                                <Slider
                                    value={overlay.position.y}
                                    onChange={(_, value) => updateOverlay({ 
                                        position: { ...overlay.position, y: value as number } 
                                    })}
                                    min={0}
                                    max={100}
                                />
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* Advanced Section */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            mb: showAdvanced ? 2 : 0 
                        }}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            Advanced Options
                        </Typography>
                        {showAdvanced ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                    
                    <Collapse in={showAdvanced}>
                        {/* Letter Spacing */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant='caption' color='text.secondary' gutterBottom>
                                Letter Spacing: {overlay.letterSpacing}px
                            </Typography>
                            <Slider
                                value={overlay.letterSpacing}
                                onChange={(_, value) => updateOverlay({ letterSpacing: value as number })}
                                min={0}
                                max={20}
                                step={0.5}
                            />
                        </Box>
                        
                        {/* Text Shadow */}
                        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={overlay.textShadow.enabled}
                                        onChange={(e) => updateOverlay({ 
                                            textShadow: { ...overlay.textShadow, enabled: e.target.checked } 
                                        })}
                                        size='small'
                                    />
                                }
                                label='Text Shadow'
                            />
                            
                            <Collapse in={overlay.textShadow.enabled}>
                                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant='caption'>Color:</Typography>
                                        <input
                                            type='color'
                                            value={overlay.textShadow.color.startsWith('rgba') ? '#000000' : overlay.textShadow.color}
                                            onChange={(e) => updateOverlay({ 
                                                textShadow: { ...overlay.textShadow, color: e.target.value } 
                                            })}
                                            style={{ width: 30, height: 24, border: 'none', cursor: 'pointer' }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 100 }}>
                                        <Typography variant='caption'>Blur: {overlay.textShadow.blur}px</Typography>
                                        <Slider
                                            size='small'
                                            value={overlay.textShadow.blur}
                                            onChange={(_, value) => updateOverlay({ 
                                                textShadow: { ...overlay.textShadow, blur: value as number } 
                                            })}
                                            min={0}
                                            max={30}
                                        />
                                    </Box>
                                </Box>
                            </Collapse>
                        </Paper>
                        
                        {/* Vignette */}
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={overlay.vignette.enabled}
                                        onChange={(e) => updateOverlay({ 
                                            vignette: { ...overlay.vignette, enabled: e.target.checked } 
                                        })}
                                        size='small'
                                    />
                                }
                                label='Vignette Effect'
                            />
                            
                            <Collapse in={overlay.vignette.enabled}>
                                <Box sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <Typography variant='caption'>Color:</Typography>
                                        <input
                                            type='color'
                                            value={overlay.vignette.color}
                                            onChange={(e) => updateOverlay({ 
                                                vignette: { ...overlay.vignette, color: e.target.value } 
                                            })}
                                            style={{ width: 30, height: 24, border: 'none', cursor: 'pointer' }}
                                        />
                                    </Box>
                                    <Typography variant='caption'>Intensity: {Math.round(overlay.vignette.intensity * 100)}%</Typography>
                                    <Slider
                                        size='small'
                                        value={overlay.vignette.intensity}
                                        onChange={(_, value) => updateOverlay({ 
                                            vignette: { ...overlay.vignette, intensity: value as number } 
                                        })}
                                        min={0}
                                        max={1}
                                        step={0.05}
                                    />
                                </Box>
                            </Collapse>
                        </Paper>
                    </Collapse>
                </Collapse>
        </>
    );
};

export default HeaderTextOverlay;
